<template>
  <div class="filter-price-content">
    <div class="filter-price-content__input-group">
      <UiInput
        :value="formattedPrices[0]"
        :placeholder="fromPricePlaceholder"
        @inputValue="(value: string | number) => updateRawValue(String(value), 0)"
        @focusin="focusInHandler"
        @focusout="() => focusOutHandler(0)"
      />
      <UiInput
        :value="formattedPrices[1]"
        :placeholder="toPricePlaceholder"
        @inputValue="(value: string | number) => updateRawValue(String(value), 1)"
        @focusin="focusInHandler"
        @focusout="() => focusOutHandler(1)"
      />
    </div>
    <ExcursionRangeInput
      :histogram
      :min="minPrice"
      :max="maxPrice"
      :value="rawValue"
      :is-modal="isModal"
      @input="updateRangeInput"
    />
  </div>
</template>

<script setup lang="ts">
  import { UiInput } from '@experience/core-input-ui';
  import ExcursionRangeInput from './ExcursionRangeInput.vue';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import { tripsterStandard } from '@experience/money-utils';
  import { locales } from '../../i18n';
  import { useComponentI18n } from '@/shared/composables/useComponentI18n';

  interface Props {
    histogram: Record<string, number>;
    minPrice: number;
    maxPrice: number;
    isModal?: boolean;
  }

  type TRawPrice = Array<number>;

  const props = withDefaults(defineProps<Props>(), {
    isModal: false,
  });

  const { t } = useComponentI18n(locales);

  const filtersStore = useExcursionFiltersStore();

  // Задаем граничные значения фильтра цен
  const rawValue = ref<TRawPrice>([
    filtersStore.state.price[0] ? filtersStore.state.price[0] : props.minPrice,
    filtersStore.state.price[1] ? filtersStore.state.price[1] : props.maxPrice,
  ]);

  const fromPricePlaceholder = computed(() => {
    const priceFmt = props.minPrice ? tripsterStandard(props.minPrice) : props.minPrice;

    return t('price.fromPriceTitle', { price: priceFmt });
  });

  const toPricePlaceholder = computed(() => {
    const priceFmt = props.maxPrice ? tripsterStandard(props.maxPrice) : props.maxPrice;

    return t('price.toPriceTitle', { price: priceFmt });
  });

  // Преобрауем эл-ты rawValue к нужному типографическому виду
  const formattedPrices = computed(() => {
    return rawValue.value.map((price) => {
      if (price) {
        return tripsterStandard(price);
      }

      return price;
    });
  });

  // Обновляем store, делаем проверку на конечные значения (чтобы не было срабатываания фильтра сразу)
  const updateStore = () => {
    const firstPrice = rawValue.value[0] === props.minPrice ? null : rawValue.value[0];
    const secondPrice = rawValue.value[1] === props.maxPrice ? null : rawValue.value[1];
    filtersStore.state.price = [firstPrice, secondPrice];
  };

  // Обновляем rawValue после ввода новой цены в input
  const updateRawValue = (value: string, index: number) => {
    const newValueForRaw = [...rawValue.value] as TRawPrice;
    const parseValue = value.replaceAll(/\D/g, '');

    newValueForRaw[index] = parseValue.length ? Number(parseValue) : 0;

    const firstValueInput = newValueForRaw[0];
    const secondValueInput = newValueForRaw[1];
    rawValue.value = [firstValueInput, secondValueInput];
    updateStore();
  };

  const updateRangeInput = (value: Array<number>) => {
    rawValue.value = [...value];
  };

  // логика для очистки инпута при вводе, и валидированном заполнении при расфокусе
  const isFocusInput = ref(false);

  const focusInHandler = () => {
    isFocusInput.value = true;
  };

  const focusOutHandler = (id: number) => {
    isFocusInput.value = false;

    const currentRawValue = rawValue.value[id];

    const isToValue = Boolean(id);

    if (!currentRawValue) {
      rawValue.value[id] = Boolean(id) ? props.maxPrice : props.minPrice;

      return;
    }

    if (isToValue) {
      validateToValue();
    } else {
      validateFromValue();
    }
  };

  const saveOrSwapValues = (from: number, to: number) => {
    if (from > to) {
      rawValue.value = [to, from];

      return;
    }

    rawValue.value = [from, to];
  };

  const validateFromValue = () => {
    const [from, to] = rawValue.value;

    saveOrSwapValues(Math.max(from ?? props.minPrice, props.minPrice), to);
  };

  const validateToValue = () => {
    const [from, to] = rawValue.value;

    saveOrSwapValues(from, Math.min(to ?? props.maxPrice, props.maxPrice));
  };

  watch(
    () => filtersStore.state.price,
    ([startPrice, endPrice]) => {
      rawValue.value = [
        startPrice !== null ? startPrice : props.minPrice,
        endPrice !== null ? endPrice : props.maxPrice,
      ];
    },
    { immediate: true },
  );
  // Нужно обновлять rawValue при смене slug, потому что иначе при переходе между регонами кешировалось значение
  watch(
    [() => props.minPrice, () => props.maxPrice],
    ([minPrice, maxPrice]) => {
      rawValue.value = [
        filtersStore.state.price[0] !== null ? filtersStore.state.price[0] : minPrice,
        filtersStore.state.price[1] !== null ? filtersStore.state.price[1] : maxPrice,
      ];
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@/shared/styles/components/input' as input;

  .filter-price-content {
    display: flex;
    flex-direction: column;
  }

  .filter-price-content__input-group {
    display: flex;

    @include input.joined-inputs;
  }
</style>
